import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {pluralDays, pluralForm, pluralGroups, pluralItems, pluralWears} from '../utils/plural';
// F12: сумма от миллиона показывается компактно, точное значение — в title.
import {money as formatMoneyPair} from '../utils/money';
import {useWardrobe} from "../context/WardrobeContext";
import Spinner from "../components/Spinner";
import WearAnalyticsSection from "../components/WearAnalyticsSection";
import CategoryTrendsSection from "../components/CategoryTrendsSection";
import ColorPaletteSection from "../components/ColorPaletteSection";
import AuthImage from "../components/AuthImage";
import DisposalTab from "../components/DisposalTab";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

// ── API Types ─────────────────────────────────────────────────────────────────

interface AnalyticsItem {
    id: string;
    name: string;
    brandName: string | null;
    categoryName: string | null;
    primaryPhotoUrl: string | null;
    totalWears: number | null;
    daysSinceWorn: number | null;
    reason: string | null;
    costPerWear: number | null;
    /** Куда ведёт рекомендация («собрать образ», «проверить состояние»); нет поля — только ссылка на вещь. */
    actionRoute?: string | null;
    /** Подпись кнопки перехода; без неё берётся нейтральное «Перейти». */
    actionLabel?: string | null;
}

// Числа KPI приходят из единого снимка сигналов (wardrobe-signals): те же, что на главной,
// в каталоге и в очереди выбытия. Дубли могут быть «не посчитаны» — тогда duplicatesCount == null.
// Точно так же «не носились дольше порога» не считается, пока истории мало: тогда бэкенд
// присылает null, а не 0 (Q6) — ноль на 272 ненадетых вещах читался как «всё в порядке».
interface WardrobeKpi {
    totalItems: number;
    activeItems: number;
    neverWornCount?: number;
    unusedDaysCount: number | null;
    unusedDaysThreshold: number;
    /** Сколько дней ещё копить историю до первой оценки; нет поля — считаем от порога. */
    historyDaysRemaining?: number | null;
    duplicatesCount: number | null;
    duplicatesUnindexedCount?: number;
    duplicatesNotComputedReason?: string | null;
    /** F03: NOT_CHECKED | PARTIAL | NO_MATCHES | MATCHES. */
    duplicateCheckState?: string | null;
    /** Охват векторного индекса, проценты. */
    duplicateCoveragePercent?: number;
    attentionCount: number;
    disposalCandidatesCount: number;
    insufficientHistory?: boolean;
    computedAt?: string | null;
}

interface IndexComponent {
    key: string;
    labelRu: string;
    /** null — подметрика не посчитана (см. notComputedReason) и не входит в вес. */
    score: number | null;
    weight: number;
    hint?: string | null;
    notComputedReason?: string | null;
}

interface WardrobeIndex {
    score: number;
    label: string;
    formulaLabel?: string | null;
    components: IndexComponent[];
}

interface UsageGroup {
    group: string;
    labelRu: string;
    count: number;
    percent: number;
}

interface UsageStats {
    distribution: UsageGroup[];
    topUsed: AnalyticsItem[];
    /** Ни разу не носили (0 носок, без окна). */
    neverWorn?: AnalyticsItem[];
    neverWornLabel?: string;
    /** Носили, но последняя носка раньше порога. */
    notWornRecently?: AnalyticsItem[];
    notWornRecentlyLabel?: string;
    /** Dead-stock по единой политике. */
    unused: AnalyticsItem[];
    unusedLabel?: string;
    unusedDaysThreshold?: number;
    insufficientHistory?: boolean;
}

const NOT_COMPUTED_LABELS: Record<string, string> = {
    INSUFFICIENT_HISTORY: 'Недостаточно истории носки',
    UNINDEXED: 'Не посчитано: есть вещи без индекса',
};

interface CategoryStructureItem {
    code: string | null;
    nameRu: string;
    count: number;
    percent: number;
    benchmarkPercent: number | null;
    delta: number | null;
}

interface CategoryStructure {
    groups: CategoryStructureItem[];
    categories: CategoryStructureItem[];
}

interface MoneyStats {
    totalValueRub: number | null;
    avgCostPerItem: number | null;
    avgCostPerWear: number | null;
    bestValue: AnalyticsItem[];
    worstValue: AnalyticsItem[];
}

interface OutfitCoverage {
    totalItems: number;
    itemsInOutfits: number;
    itemsNotInOutfits: number;
    underrepresented: AnalyticsItem[];
    /** Сколько «нераскрытых» всего и есть ли что показать сверх присланного среза. */
    underrepresentedTotal?: number | null;
    hasMore?: boolean;
}

interface ConditionCount {
    code: string;
    nameRu: string;
    count: number;
}

interface WearConditionStats {
    conditions: ConditionCount[];
    topDefects: ConditionCount[];
}

interface Recommendations {
    dispose: AnalyticsItem[];
    buy: string[];
    wearMore: AnalyticsItem[];
}

interface WardrobeDashboard {
    kpi: WardrobeKpi;
    index: WardrobeIndex;
    usage: UsageStats;
    structure: CategoryStructure;
    money: MoneyStats;
    outfitCoverage: OutfitCoverage;
    wearCondition: WearConditionStats;
    recommendations: Recommendations;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * F03: пустой список совпадений сам по себе ничего не значит — значение появляется только
 * рядом с охватом. Четыре состояния вместо «число или прочерк».
 */
const DUPLICATE_STATE_TEXT: Record<string, string> = {
    NOT_CHECKED: 'Не проверено',
    PARTIAL: 'Проверено частично',
    NO_MATCHES: 'Совпадений нет',
    MATCHES: 'Есть похожие',
};

type Tab = 'usage' | 'structure' | 'money' | 'outfits' | 'wear' | 'duplicates' | 'disposal' | 'recommendations';

const TABS: {key: Tab; label: string}[] = [
    {key: 'usage', label: 'Использование'},
    {key: 'structure', label: 'Структура'},
    {key: 'money', label: 'Деньги'},
    {key: 'outfits', label: 'Образы'},
    {key: 'wear', label: 'Износ'},
    {key: 'duplicates', label: 'Дубликаты'},
    {key: 'disposal', label: 'Выбытие'},
    {key: 'recommendations', label: 'Рекомендации'},
];

const fmt = (n: number | null, suffix = '') =>
    n == null ? '—' : n.toLocaleString('ru-RU') + suffix;

/**
 * Подпись плитки, пока история носки не набрана (Q6). Сколько именно ждать, знает бэкенд
 * (`historyDaysRemaining`); пока поля нет — называем порог, от которого считается срок.
 */
const historyPendingLabel = (kpi: {unusedDaysThreshold: number; historyDaysRemaining?: number | null}): string =>
    `история копится ещё ${pluralDays(kpi.historyDaysRemaining ?? kpi.unusedDaysThreshold)}`;

const indexColor = (score: number) => {
    if (score >= 90) return 'text-emerald-500';
    if (score >= 75) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
};

const indexStroke = (score: number) => {
    if (score >= 90) return '#10b981';
    if (score >= 75) return '#22c55e';
    if (score >= 60) return '#eab308';
    if (score >= 40) return '#f97316';
    return '#ef4444';
};

const componentColor = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-400';
    if (score >= 40) return 'bg-orange-400';
    return 'bg-red-400';
};

const deltaColor = (delta: number | null) => {
    if (delta == null) return '';
    if (delta > 3) return 'text-rose-500 dark:text-rose-400';
    if (delta < -3) return 'text-sky-500 dark:text-sky-400';
    return 'text-gray-500 dark:text-gray-400';
};

// Подсказки подметрик индекса приходят с бэка (component.hint, index.formulaLabel):
// один текст для web и iOS, порог в подсказке совпадает с порогом в KPI.

/** Отступ подсказки от края экрана — тот же боковой «жёлоб», что у контента страницы. */
const TIP_GUTTER = 16;
const TIP_MAX_WIDTH = 240;

/**
 * Подсказка «Как считается» (Q12). Раньше она всегда жила в разметке как `absolute … w-60`
 * с `opacity-0`: невидимая подсказка у правого края всё равно расширяла страницу, и на 390 px
 * аналитика получала горизонтальный скролл (`scrollWidth` 584). Теперь всплывающая часть
 * рендерится только на время показа и позиционируется `fixed` — от края экрана, с шириной не
 * больше `100vw - 2rem`, поэтому в поток страницы она не попадает вовсе.
 */
const InfoTooltip: React.FC<{text: string}> = ({text}) => {
    const anchorRef = useRef<HTMLSpanElement>(null);
    const [box, setBox] = useState<{top: number; left: number; width: number} | null>(null);

    const show = useCallback(() => {
        const rect = anchorRef.current?.getBoundingClientRect();
        const viewport = typeof window === 'undefined' ? TIP_MAX_WIDTH : window.innerWidth;
        const width = Math.max(120, Math.min(TIP_MAX_WIDTH, viewport - TIP_GUTTER * 2));
        const desired = (rect?.left ?? TIP_GUTTER) - 8;
        const left = Math.min(Math.max(TIP_GUTTER, desired), Math.max(TIP_GUTTER, viewport - TIP_GUTTER - width));
        setBox({top: rect?.top ?? TIP_GUTTER, left, width});
    }, []);

    const hide = useCallback(() => setBox(null), []);

    return (
        <span className="relative inline-flex items-center ml-1.5 shrink-0">
            <span
                ref={anchorRef}
                className="cursor-help w-3.5 h-3.5 rounded-full border border-gray-400 dark:border-gray-500 text-gray-400 dark:text-gray-500 flex items-center justify-center text-[9px] leading-none hover:border-gray-600 dark:hover:border-gray-300 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                tabIndex={0}
                role="button"
                aria-label="Как считается"
                onPointerEnter={show}
                onPointerLeave={hide}
                onFocus={show}
                onBlur={hide}
                onKeyDown={e => {
                    if (e.key === 'Escape') hide();
                }}
            >
                ?
            </span>
            {/* Текст всегда доступен скринридеру и поиску по странице; ширину страницы
                `sr-only` не меняет — в отличие от прежнего невидимого блока `w-60`. */}
            <span className="sr-only">{text}</span>
            {box && (
                <span
                    aria-hidden="true"
                    data-testid="info-tooltip"
                    style={{top: box.top - 8, left: box.left, width: box.width}}
                    className="fixed -translate-y-full max-w-[calc(100vw-2rem)] rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-xs px-3 py-2.5 leading-relaxed pointer-events-none z-50 shadow-xl whitespace-pre-line"
                >
                    {text}
                </span>
            )}
        </span>
    );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ItemRow: React.FC<{
    item: AnalyticsItem; extra?: string; actionRoute?: string; actionLabel?: string;
}> = ({item, extra, actionRoute = item.actionRoute ?? undefined, actionLabel = item.actionLabel ?? undefined}) => (
    <li className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-ink-700 p-3">
        {item.primaryPhotoUrl ? (
            <AuthImage
                imageKey={item.primaryPhotoUrl}
                alt={item.name}
                variant="thumbnail"
                className="w-10 h-10 rounded-lg object-cover shrink-0"
            />
        ) : (
            <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-ink-700 shrink-0"/>
        )}
        <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</p>
            {item.brandName && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.brandName}</p>
            )}
        </div>
        {extra && <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{extra}</span>}
        {/* Q10: у рекомендации из сигналов есть своё действие — ведём туда, а не «в карточку». */}
        {actionRoute && (
            <Link
                to={actionRoute}
                data-testid="item-action"
                className="shrink-0 inline-flex items-center min-h-[44px] px-3 rounded-xl border border-brand-200 dark:border-brand-700 text-brand-600 dark:text-brand-400 text-xs font-medium hover:bg-brand-50 dark:hover:bg-brand-900/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
                {actionLabel || 'Перейти'}
            </Link>
        )}
        <Link
            to={`/clothing/${item.id}`}
            aria-label={`Открыть «${item.name}»`}
            className="shrink-0 inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-brand-600 dark:text-brand-400 text-sm hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-xl"
        >
            →
        </Link>
    </li>
);

const DonutGauge: React.FC<{score: number}> = ({score}) => {
    const r = 58, cx = 70, cy = 70;
    const circumference = 2 * Math.PI * r;
    const dashOffset = circumference * (1 - Math.min(100, Math.max(0, score)) / 100);
    return (
        <svg width="140" height="140" role="img" aria-label={`Индекс гардероба: ${score}`}>
            <circle cx={cx} cy={cy} r={r} fill="none"
                className="stroke-gray-100 dark:stroke-ink-700" strokeWidth="14"/>
            <circle cx={cx} cy={cy} r={r} fill="none"
                stroke={indexStroke(score)} strokeWidth="14"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${cx} ${cy})`}
                style={{transition: 'stroke-dashoffset 0.6s ease'}}
            />
        </svg>
    );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const Insights: React.FC = () => {
    const {selectedOwnerId, selectedOwnerDisplayName, isOwnWardrobe} = useWardrobe();
    const [data, setData] = useState<WardrobeDashboard | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>('usage');

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.get<WardrobeDashboard>('/analytics/dashboard', {params});
            setData(res.data);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId]);

    useDocumentTitle();

    useEffect(() => {load();}, [load]);

    const title = isOwnWardrobe
        ? 'Аналитика гардероба'
        : `Аналитика: ${selectedOwnerDisplayName || (selectedOwnerId?.slice(0, 8) + '...')}`;

    if (loading) return <Spinner/>;

    if (error) return (
        <div className="p-6 text-center">
            <p className="text-gray-600 dark:text-gray-300 mb-4">Не удалось загрузить аналитику</p>
            <button onClick={load}
                className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700">
                Повторить
            </button>
        </div>
    );

    const d = data!;
    const {kpi, index, usage, structure, money, outfitCoverage, wearCondition, recommendations} = d;

    // ── AI Insights sidebar content (derived from dashboard data)
    const aiInsights: {icon: string; text: string; sub: string}[] = [];
    const duplicatesComputed = kpi.duplicatesCount != null;
    /** Q6: срок «не носили» посчитан только когда истории хватило и число пришло. */
    const unusedKnown = kpi.unusedDaysCount != null && !kpi.insufficientHistory;
    const unindexed = kpi.duplicatesUnindexedCount ?? 0;
    if (duplicatesComputed && (kpi.duplicatesCount ?? 0) > 0)
        aiInsights.push({icon: '⚠', text: `${pluralGroups(kpi.duplicatesCount ?? 0)} взаимозаменяемых вещей`, sub: 'Рекомендуем не покупать похожие вещи.'});
    if (recommendations.buy.length > 0)
        aiInsights.push({icon: '⚠', text: `Нет: ${recommendations.buy[0]}`, sub: 'В вашем гардеробе не хватает этой категории.'});
    const unusedCount = kpi.unusedDaysCount;
    if (unusedCount != null && unusedCount > 0)
        aiInsights.push({icon: '●', text: `${pluralItems(unusedCount)} не ${pluralForm(unusedCount, 'используется', 'используются', 'используются')}`, sub: `Более ${pluralDays(kpi.unusedDaysThreshold)}. Возможно, стоит пересмотреть их.`});
    if (kpi.insufficientHistory)
        aiInsights.push({icon: '●', text: 'Недостаточно истории носки', sub: `Отмечайте носки — через ${pluralDays(kpi.unusedDaysThreshold)} используемость будет посчитана.`});
    if (money.avgCostPerWear != null)
        aiInsights.push({icon: '↗', text: 'Средняя стоимость использования', sub: `${fmt(money.avgCostPerWear)} ₽ за носку`});
    if (kpi.attentionCount > 0)
        aiInsights.push({icon: '⚠', text: `${pluralItems(kpi.attentionCount)} ${pluralForm(kpi.attentionCount, 'требует', 'требуют', 'требуют')} внимания`, sub: 'Износ, пятна или повреждения.'});

    return (
        <div className="p-4 md:p-6 space-y-5">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>

            {/* KPI row — карточки-ссылки к спискам вещей (FR-3, BR-1) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="analytics-kpi">
                <KpiCard title="Всего вещей" value={String(kpi.totalItems)}
                    sub={`Активных: ${kpi.activeItems}`} icon="👔" to="/clothing"/>
                {/* Q6: «0» при нехватке истории читался как «всё в порядке» — показываем «—». */}
                {unusedKnown
                    ? <KpiCard title={`Не носились >${kpi.unusedDaysThreshold} дн.`}
                        value={String(unusedCount)}
                        sub={`${kpi.activeItems ? Math.round((unusedCount ?? 0) * 100 / kpi.activeItems) : 0}% от гардероба`}
                        icon="⏰" warn={(unusedCount ?? 0) > 0}
                        to={`/clothing?notWornDays=${kpi.unusedDaysThreshold}`}/>
                    : <KpiCard title={`Не носились >${kpi.unusedDaysThreshold} дн.`}
                        value="—" sub={historyPendingLabel(kpi)} icon="⏰"
                        to={`/clothing?notWornDays=${kpi.unusedDaysThreshold}`}/>}
                {duplicatesComputed
                    ? <KpiCard title="Дубликаты" value={String(kpi.duplicatesCount)}
                        sub={(kpi.duplicatesCount ?? 0) > 0 ? 'Можно оптимизировать' : 'Проверено — совпадений нет'} icon="⊞"
                        warn={(kpi.duplicatesCount ?? 0) > 0}
                        onClick={() => setActiveTab('duplicates')}/>
                    /* R2: без индекса дубли не 0 и не 100 — «не посчитано», ссылка на индексацию */
                    : <KpiCard title="Дубликаты" value="—"
                        sub={`${DUPLICATE_STATE_TEXT[kpi.duplicateCheckState ?? 'NOT_CHECKED'] ?? 'Не проверено'}`
                            + ` · охват ${kpi.duplicateCoveragePercent ?? 0}% · ${unindexed} без индекса`}
                        icon="⊞" warn to="/indexing"/>}
                <KpiCard title="Требуют внимания" value={String(kpi.attentionCount)}
                    sub="Износ, пятна, ремонт" icon="⚠" warn={kpi.attentionCount > 0}
                    to="/clothing?attention=true"/>
                <KpiCard title="Кандидаты на выбытие" value={String(kpi.disposalCandidatesCount)}
                    sub="Рекомендуем" icon="🗑" warn={kpi.disposalCandidatesCount > 0}
                    to="/disposal"/>
            </div>

            {/* Index + AI Insights */}
            <div className="grid lg:grid-cols-3 gap-4">
                {/* Wardrobe Index */}
                <div className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-ink-700 p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                            Индекс гардероба
                            {index.formulaLabel && <InfoTooltip text={index.formulaLabel}/>}
                        </h2>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                        <div className="flex flex-col items-center shrink-0">
                            <div className="relative">
                                <DonutGauge score={index.score}/>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className={`text-3xl font-bold ${indexColor(index.score)}`}>{index.score}</span>
                                    <span className="text-xs text-gray-400">/100</span>
                                </div>
                            </div>
                            <p className={`text-sm font-medium mt-1 ${indexColor(index.score)}`}>{index.label}</p>
                        </div>
                        <div className="flex-1 w-full space-y-3">
                            {index.components.map(c => (
                                <div key={c.key} data-testid={`index-component-${c.key}`}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-gray-700 dark:text-gray-300 flex items-center">
                                            {c.labelRu}
                                            {c.hint && <InfoTooltip text={c.hint}/>}
                                        </span>
                                        {c.score == null
                                            ? <span className="text-gray-400 dark:text-gray-500 text-xs"
                                                data-testid={`index-not-computed-${c.key}`}>
                                                {NOT_COMPUTED_LABELS[c.notComputedReason ?? ''] ?? 'Не посчитано'}
                                            </span>
                                            : <span className="text-gray-500 dark:text-gray-400 font-medium">{c.score}/100</span>}
                                    </div>
                                    <div className="h-2 rounded-full bg-gray-100 dark:bg-ink-700 overflow-hidden">
                                        {c.score != null && (
                                            <div className={`h-full w-full rounded-full origin-left ${componentColor(c.score)}`}
                                                style={{transform: `scaleX(${c.score / 100})`, transition: 'transform 0.5s ease'}}/>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* AI Insights sidebar */}
                {aiInsights.length > 0 && (
                    <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            <span className="text-brand-500">✦</span> AI-инсайты
                        </h2>
                        <ul className="space-y-3">
                            {aiInsights.map((ins, i) => (
                                <li key={i} className="flex gap-3 items-start">
                                    <span className="text-yellow-500 mt-0.5 shrink-0">{ins.icon}</span>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{ins.text}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{ins.sub}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Tab bar: своя горизонтальная прокрутка без полосы — страница на 390 px
                не должна ездить вбок из-за восьми вкладок (Q12). */}
            <div className="overflow-x-auto no-scrollbar -mx-4 px-4">
                <div className="flex gap-0 border-b border-gray-200 dark:border-ink-700 min-w-max">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setActiveTab(t.key)}
                            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                activeTab === t.key
                                    ? 'border-brand-600 text-brand-600 dark:text-brand-400 dark:border-brand-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab content */}
            <div data-testid={`tab-${activeTab}`}>
                {activeTab === 'usage' && <UsageTab usage={usage} ownerId={selectedOwnerId}
                    unusedDaysThreshold={kpi.unusedDaysThreshold}/>}
                {activeTab === 'structure' && <StructureTab structure={structure} ownerId={selectedOwnerId}/>}
                {activeTab === 'money' && <MoneyTab money={money}/>}
                {activeTab === 'outfits' && <OutfitsTab coverage={outfitCoverage}/>}
                {activeTab === 'wear' && <WearTab wearCondition={wearCondition}/>}
                {activeTab === 'duplicates' && <DuplicatesTab ownerId={selectedOwnerId}/>}
                {activeTab === 'disposal' && <DisposalTab ownerId={selectedOwnerId}/>}
                {activeTab === 'recommendations' && <RecommendationsTab rec={recommendations}/>}
            </div>
        </div>
    );
};

// ── KPI Card ──────────────────────────────────────────────────────────────────

// FR-3 (BR-1): KPI-карточка — реальная ссылка (`to`) или кнопка (`onClick`) к списку
// вещей; доступность — focus-стили и тач-зона ≥ 44px.
const KpiCard: React.FC<{
    title: string; value: string; sub: string; icon: string; warn?: boolean;
    to?: string; onClick?: () => void;
}> = ({title, value, sub, icon, warn, to, onClick}) => {
    const cls = `block w-full text-left rounded-xl border p-4 space-y-1 min-h-[44px] transition-colors ${warn
        ? 'border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/10'
        : 'border-gray-200 dark:border-ink-700'} ${(to || onClick)
        ? 'hover:border-brand-400 dark:hover:border-brand-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 cursor-pointer'
        : ''}`;
    const inner = (
        <>
            <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
                <span className="text-lg">{icon}</span>
            </div>
            <p className={`text-2xl font-bold ${warn ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'}`}>
                {value}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">{sub}</p>
        </>
    );
    if (to) {
        return <Link to={to} className={cls} data-testid={`kpi-link-${title}`}>{inner}</Link>;
    }
    if (onClick) {
        return <button type="button" onClick={onClick} className={cls} data-testid={`kpi-button-${title}`}>{inner}</button>;
    }
    return <div className={cls}>{inner}</div>;
};

// ── Usage Tab ─────────────────────────────────────────────────────────────────

const GROUP_COLORS: Record<string, string> = {
    OFTEN: 'bg-emerald-500', REGULAR: 'bg-green-400', RARELY: 'bg-yellow-400',
    NEVER_WORN: 'bg-orange-400', VERY_RARELY: 'bg-orange-400', UNUSED: 'bg-red-400',
};

/** Список вещей с «+N ещё» в каталог по порогу; пустой список — короткая подпись. */
const UsageList: React.FC<{
    testId: string; title: string; items: AnalyticsItem[]; empty: string; moreTo?: string;
}> = ({testId, title, items, empty, moreTo}) => (
    <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-3" data-testid={testId}>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {title} ({items.length})
        </h3>
        {items.length === 0
            ? <p className="text-sm text-gray-400">{empty}</p>
            : <>
                <ul className="space-y-2">
                    {items.slice(0, 5).map(item => (
                        <ItemRow key={item.id} item={item}
                            extra={item.daysSinceWorn != null ? `${item.daysSinceWorn} дн.` : undefined}/>
                    ))}
                </ul>
                {items.length > 5 && moreTo && (
                    /* FR-3: «+N ещё» ведёт в гардероб с deep-link фильтром по порогу */
                    <Link to={moreTo}
                        className="flex items-center justify-center min-h-[44px] text-xs text-brand-600 dark:text-brand-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-lg">
                        +{items.length - 5} ещё
                    </Link>
                )}
            </>
        }
    </div>
);

const UsageTab: React.FC<{usage: UsageStats; ownerId: string | null; unusedDaysThreshold: number}> = (
    {usage, ownerId, unusedDaysThreshold}
) => {
    const threshold = usage.unusedDaysThreshold ?? unusedDaysThreshold;
    const neverWorn = usage.neverWorn ?? [];
    const notWornRecently = usage.notWornRecently ?? usage.unused ?? [];
    const allWornRegularly = !usage.insufficientHistory && neverWorn.length === 0 && notWornRecently.length === 0;
    return (
    <div className="space-y-6">
        {/* QA-3: «все носятся регулярно» — только когда оба списка пусты и истории достаточно */}
        {usage.insufficientHistory && (
            <p className="text-sm text-amber-700 dark:text-amber-300 rounded-xl border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/10 p-3"
                data-testid="usage-insufficient-history">
                Недостаточно истории: у большинства вещей ещё нет отметок носки, поэтому используемость не считается.
                Отмечайте носки — через {pluralDays(threshold)} появится оценка.
            </p>
        )}
        {allWornRegularly && (
            <p className="text-sm text-emerald-700 dark:text-emerald-300" data-testid="usage-all-regular">
                Все вещи носятся регулярно
            </p>
        )}
        <div className="grid md:grid-cols-3 gap-4">
            {/* Frequency distribution */}
            <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Как часто носите вещи</h3>
                {usage.distribution.map(g => (
                    <div key={g.group}>
                        <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-700 dark:text-gray-300">{g.labelRu}</span>
                            <span className="text-gray-500 dark:text-gray-400">{g.count}</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 dark:bg-ink-700 overflow-hidden">
                            <div className={`h-full rounded-full ${GROUP_COLORS[g.group] ?? 'bg-gray-400'}`}
                                style={{width: `${g.percent}%`}}/>
                        </div>
                    </div>
                ))}
            </div>

            {/* Top used */}
            <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Топ используемых вещей</h3>
                {usage.topUsed.length === 0
                    ? <p className="text-sm text-gray-400">Нет данных о носках</p>
                    : <ul className="space-y-2">
                        {usage.topUsed.map(item => (
                            <ItemRow key={item.id} item={item}
                                extra={item.totalWears != null ? pluralWears(item.totalWears) : undefined}/>
                        ))}
                    </ul>
                }
            </div>

            {/* Не носили давно: носили, но раньше порога (подпись и порог — с бэка) */}
            <UsageList testId="usage-not-worn-recently"
                title={usage.notWornRecentlyLabel ?? `Не носили более ${pluralDays(threshold)}`}
                items={notWornRecently}
                empty={usage.insufficientHistory ? 'Недостаточно истории' : 'Нет таких вещей'}
                moreTo={`/clothing?notWornDays=${threshold}`}/>
        </div>

        {/* Ни разу не носили: факт (0 носок), без окна — отдельно от «давно не носили» */}
        <UsageList testId="usage-never-worn"
            title={usage.neverWornLabel ?? 'Ни разу не носили'}
            items={neverWorn}
            empty="Все вещи надевали хотя бы раз"/>

        <WearAnalyticsSection ownerId={ownerId}/>
    </div>
    );
};

// ── Structure Tab ─────────────────────────────────────────────────────────────

/**
 * Эталон по группам — та же таблица, что в `WardrobeAnalyticsServiceImpl.GROUP_BENCHMARK`
 * (30/20/15/10/10/15). Дублируется на клиенте только как запасной вариант: если бэкенд
 * прислал `benchmarkPercent`, берётся его значение.
 */
const GROUP_BENCHMARK: Record<string, number> = {
    TOPS: 30, BOTTOMS: 20, OUTERWEAR: 15, FOOTWEAR: 10, ACCESSORIES: 10, OTHER: 15,
};

/**
 * Строка сравнения «факт против эталона» (Q15). Раньше факт показывался по группам, а
 * «рекомендуемое распределение» — по типам вещей, и сравнить было нечего. Теперь обе
 * величины стоят на одной оси и в одном масштабе: длина полосы — доля от гардероба,
 * верхняя полоса — как есть, нижняя — как советует эталон.
 */
const StructureRow: React.FC<{item: CategoryStructureItem}> = ({item}) => {
    const benchmark = item.benchmarkPercent ?? GROUP_BENCHMARK[item.code ?? ''] ?? null;
    const delta = item.delta ?? (benchmark != null ? item.percent - benchmark : null);
    const scale = (pct: number) => `${Math.min(100, pct * 2)}%`;
    return (
        <div data-testid={`structure-row-${item.code ?? item.nameRu}`} className="space-y-1">
            <div className="flex justify-between items-baseline text-xs">
                <span className="text-gray-700 dark:text-gray-300">{item.nameRu}</span>
                <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                    {item.percent}%
                    {benchmark != null && <span className="text-gray-400"> · эталон {benchmark}%</span>}
                    {delta != null && (
                        <span className={`ml-1 font-medium ${deltaColor(delta)}`}>
                            {delta > 0 ? `+${delta}` : delta}%
                        </span>
                    )}
                </span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 dark:bg-ink-700 overflow-hidden">
                <div className="h-full rounded-full bg-brand-500 dark:bg-brand-400"
                    style={{width: scale(item.percent)}}/>
            </div>
            {benchmark != null && (
                <div className="h-1.5 rounded-full bg-gray-100 dark:bg-ink-700 overflow-hidden">
                    <div className="h-full rounded-full bg-gray-400 dark:bg-gray-500"
                        style={{width: scale(benchmark)}}/>
                </div>
            )}
        </div>
    );
};

const StructureTab: React.FC<{structure: CategoryStructure; ownerId: string | null}> = ({structure, ownerId}) => (
    <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
            {/* Факт и эталон на одной оси (Q15) */}
            <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-3"
                data-testid="structure-benchmark">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                        Структура против эталона
                        <InfoTooltip text={'Верхняя полоса — доля группы в вашем гардеробе, нижняя серая — '
                            + 'сбалансированное распределение: Верх 30%, Низ 20%, Верхняя одежда 15%, '
                            + 'Обувь 10%, Аксессуары 10%, Другое 15%.'}/>
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">Цветная полоса — как есть, серая — как советуем</p>
                </div>
                {structure.groups.map(g => <StructureRow key={g.code ?? g.nameRu} item={g}/>)}
            </div>

            {/* Что именно лежит в гардеробе — факт по типам вещей, без эталона */}
            <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4"
                data-testid="structure-categories">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Самые многочисленные типы</h3>
                <p className="text-xs text-gray-400 mb-3">Доля типа вещей в гардеробе; эталон задан по группам слева</p>
                <div className="space-y-2">
                    {structure.categories.map(c => (
                        <div key={c.nameRu} className="flex items-center gap-2 text-xs">
                            <span className="w-32 shrink-0 text-gray-700 dark:text-gray-300 truncate">{c.nameRu}</span>
                            <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-ink-700 overflow-hidden">
                                <div className="h-full rounded-full bg-brand-400 dark:bg-brand-500"
                                    style={{width: `${Math.min(100, c.percent * 3)}%`}}/>
                            </div>
                            <span className="w-14 text-right text-gray-500 tabular-nums">
                                {c.count} · {c.percent}%
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        <CategoryTrendsSection ownerId={ownerId}/>
        <ColorPaletteSection ownerId={ownerId}/>
    </div>
);

// ── Money Tab ─────────────────────────────────────────────────────────────────

const MoneyTab: React.FC<{money: MoneyStats}> = ({money}) => (
    <div className="space-y-5">
        {money.totalValueRub == null ? (
            <p className="text-gray-400 text-sm">Добавьте цены к вещам, чтобы здесь появилась финансовая статистика.</p>
        ) : (
            <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Общая стоимость</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1"
                           title={formatMoneyPair(money.totalValueRub).title}
                           data-testid="money-total-value">
                            {formatMoneyPair(money.totalValueRub).text}
                        </p>
                    </div>
                    <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Средняя стоимость вещи</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                            {fmt(money.avgCostPerItem)} ₽
                        </p>
                    </div>
                    <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Средняя стоимость носки</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                            {money.avgCostPerWear != null ? `${fmt(money.avgCostPerWear)} ₽` : '—'}
                        </p>
                    </div>
                </div>

                {(money.bestValue.length > 0 || money.worstValue.length > 0) && (
                    <div className="grid md:grid-cols-2 gap-4">
                        {money.bestValue.length > 0 && (
                            <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4">
                                <h3 className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mb-3">Самые выгодные</h3>
                                <ul className="space-y-2">
                                    {money.bestValue.map(item => (
                                        <ItemRow key={item.id} item={item}
                                            extra={item.costPerWear != null ? `${fmt(item.costPerWear)} ₽` : undefined}/>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {money.worstValue.length > 0 && (
                            <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4">
                                <h3 className="text-sm font-semibold text-rose-600 dark:text-rose-400 mb-3">Самые невыгодные</h3>
                                <ul className="space-y-2">
                                    {money.worstValue.map(item => (
                                        <ItemRow key={item.id} item={item}
                                            extra={item.costPerWear != null ? `${fmt(item.costPerWear)} ₽` : undefined}/>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </>
        )}
    </div>
);

// ── Outfits Tab ───────────────────────────────────────────────────────────────

/** Сколько «нераскрытых» вещей показываем до нажатия «Показать все». */
const UNDERUSED_PREVIEW = 8;

const OutfitsTab: React.FC<{coverage: OutfitCoverage}> = ({coverage}) => {
    const pct = coverage.totalItems === 0 ? 0 : Math.round(coverage.itemsInOutfits * 100 / coverage.totalItems);
    const [expanded, setExpanded] = useState(false);
    const underused = coverage.underrepresented;
    const total = coverage.underrepresentedTotal ?? underused.length;
    // «Показать все» имеет смысл, пока бэкенд знает про остаток (hasMore) или сам список длиннее среза.
    const hasMore = coverage.hasMore ?? underused.length > UNDERUSED_PREVIEW;
    const shown = expanded ? underused : underused.slice(0, UNDERUSED_PREVIEW);
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{coverage.totalItems}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Всего вещей</p>
                </div>
                <div className="rounded-xl border border-green-200 dark:border-green-800/50 p-4 text-center bg-green-50 dark:bg-green-900/10">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{coverage.itemsInOutfits}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">В образах ({pct}%)</p>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{coverage.itemsNotInOutfits}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Без образов</p>
                </div>
            </div>

            {underused.length > 0 && (
                <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4" data-testid="outfits-underused">
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Нераскрытые вещи (менее 3 образов) · {total}
                        </h3>
                        {/* Q10: список без действия был тупиком — уводим в конструктор образов. */}
                        <Link to="/outfits/builder" data-testid="outfits-build-cta"
                            className="inline-flex items-center min-h-[44px] px-4 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                            Собрать образ
                        </Link>
                    </div>
                    <ul className="space-y-2">
                        {shown.map(item => (
                            <ItemRow key={item.id} item={item}
                                actionRoute={item.actionRoute ?? '/outfits/builder'}
                                actionLabel={item.actionLabel ?? 'Собрать образ'}/>
                        ))}
                    </ul>
                    {hasMore && !expanded && (
                        <button type="button" data-testid="outfits-show-all"
                            onClick={() => setExpanded(true)}
                            className="mt-3 w-full min-h-[44px] rounded-xl border border-gray-200 dark:border-ink-700 text-sm text-brand-600 dark:text-brand-400 hover:border-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                            Показать все ({total})
                        </button>
                    )}
                </div>
            )}

            {coverage.totalItems === 0 && (
                <p className="text-gray-400 text-sm">Добавьте вещи и создайте образы — здесь появится покрытие.</p>
            )}
        </div>
    );
};

// ── Wear Tab ──────────────────────────────────────────────────────────────────

const CONDITION_COLORS: Record<string, string> = {
    NEW: 'bg-emerald-500', LIKE_NEW: 'bg-green-400', EXCELLENT: 'bg-green-400',
    GOOD: 'bg-yellow-400', FAIR: 'bg-orange-400', WORN: 'bg-rose-400', DAMAGED: 'bg-red-600',
};

const WearTab: React.FC<{wearCondition: WearConditionStats}> = ({wearCondition}) => {
    const total = wearCondition.conditions.reduce((s, c) => s + c.count, 0) || 1;
    return (
        <div className="space-y-5">
            {wearCondition.conditions.length === 0 ? (
                <p className="text-gray-400 text-sm">Нет данных о состоянии вещей — заполните поле «Состояние» у вещей.</p>
            ) : (
                <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Состояние гардероба</h3>
                    {wearCondition.conditions.map(c => (
                        <div key={c.code}>
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-700 dark:text-gray-300">{c.nameRu}</span>
                                <span className="text-gray-500 dark:text-gray-400">{c.count} шт.</span>
                            </div>
                            <div className="h-2 rounded-full bg-gray-100 dark:bg-ink-700 overflow-hidden">
                                <div className={`h-full rounded-full ${CONDITION_COLORS[c.code] ?? 'bg-gray-400'}`}
                                    style={{width: `${Math.round(c.count * 100 / total)}%`}}/>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {wearCondition.topDefects.length > 0 && (
                <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Топ дефектов</h3>
                    <ul className="space-y-2">
                        {wearCondition.topDefects.map(d => (
                            <li key={d.code} className="flex justify-between items-center text-sm">
                                <span className="text-gray-700 dark:text-gray-300">{d.nameRu}</span>
                                <span className="text-gray-500 dark:text-gray-400 font-medium">{d.count} вещей</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// ── Duplicates Tab (FR-4) ─────────────────────────────────────────────────────

interface DuplicateItem {
    id: string;
    name: string;
    brandName: string | null;
    categoryName: string | null;
    primaryPhotoUrl: string | null;
    totalWears: number | null;
}

interface DuplicateGroup {
    categoryName: string | null;
    brandName: string | null;
    count: number;
    duplicateType?: string | null;
    similarity?: number | null;
    minSimilarity?: number | null;
    items: DuplicateItem[];
}

interface InsightsResponse {
    duplicateGroups: DuplicateGroup[];
    duplicatesUnindexedCount?: number;
    /** Почему групп нет: `UNINDEXED` — индекс не построен, а не «дублей нет» (Q3/Q18). */
    notComputedReason?: string | null;
    /** Сколько активных вещей ещё без векторного индекса. */
    unindexedCount?: number;
    /** F03: NOT_CHECKED | PARTIAL | NO_MATCHES | MATCHES. */
    duplicateCheckState?: string | null;
    /** Охват векторного индекса, проценты. */
    duplicateCoveragePercent?: number;
    consentRequired?: boolean;
}


const DUPLICATE_TYPE_LABELS: Record<string, string> = {
    same_item: 'Одна и та же вещь',
    visual_duplicate: 'Визуальный дубль',
    functional_duplicate: 'Функциональный дубль',
};

// Список групп дублей из существующего GET /insights. Группы строит векторная кластеризация
// на бэкенде (похожесть описаний + цветовой гейт), а не совпадение названия и бренда.
// KPI-карточка «Дубликаты» открывает вкладку.
const DuplicatesTab: React.FC<{ownerId: string | null}> = ({ownerId}) => {
    const [groups, setGroups] = useState<DuplicateGroup[] | null>(null);
    const [unindexed, setUnindexed] = useState(0);
    const [notComputed, setNotComputed] = useState<string | null>(null);
    const [checkState, setCheckState] = useState<string | null>(null);
    const [coverage, setCoverage] = useState(0);
    const [consentRequired, setConsentRequired] = useState(false);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setError(false);
        setGroups(null);
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.get<InsightsResponse>('/insights', {params});
            setGroups(res.data.duplicateGroups ?? []);
            setUnindexed(res.data.unindexedCount ?? res.data.duplicatesUnindexedCount ?? 0);
            setNotComputed(res.data.notComputedReason ?? null);
            setCheckState(res.data.duplicateCheckState ?? null);
            setCoverage(res.data.duplicateCoveragePercent ?? 0);
            setConsentRequired(!!res.data.consentRequired);
        } catch {
            setError(true);
            setGroups([]);
        }
    }, [ownerId]);

    useEffect(() => {load();}, [load]);

    if (error) return (
        <div className="p-6 text-center" data-testid="duplicates-error">
            <p className="text-gray-600 dark:text-gray-300 mb-4">Не удалось загрузить дубликаты</p>
            <button onClick={load}
                className="min-h-[44px] px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700">
                Повторить
            </button>
        </div>
    );

    if (groups === null) return <Spinner/>;

    if (consentRequired) return (
        <p className="text-gray-400 text-sm" data-testid="duplicates-consent">
            Дубликаты не строятся без согласия на AI-профилирование — включите его в настройках приватности.
        </p>
    );

    // F03: охват печатается всегда — он и есть разница между «нет совпадений» и «не искали».
    const coverageLine = (
        <p className="text-gray-400 text-xs" data-testid="duplicates-coverage">
            {DUPLICATE_STATE_TEXT[checkState ?? 'NOT_CHECKED'] ?? 'Не проверено'} · охват индексации {coverage}%
            {unindexed > 0 && ` · без индекса: ${pluralItems(unindexed)}`}
        </p>
    );

    const unindexedHint = unindexed > 0 && (
        <p className="text-gray-400 text-xs" data-testid="duplicates-unindexed">
            Ещё не проиндексировано: {pluralItems(unindexed)} — дубли по ним появятся после AI-анализа.
        </p>
    );

    // Q18: из вкладки должен быть выход к индексации — иначе «не посчитано» некуда нажать.
    const indexLink = (
        <Link to="/indexing" data-testid="duplicates-index-cta"
            className="inline-flex items-center min-h-[44px] px-4 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            Проиндексировать гардероб
        </Link>
    );

    // Q3/Q18: «индекс не построен» — это не «дублей нет». Пока бэкенд не прислал причину,
    // ориентируемся на число вещей без индекса.
    const indexMissing = notComputed === 'UNINDEXED' || (groups.length === 0 && unindexed > 0);

    if (indexMissing) return (
        <div className="space-y-3" data-testid="duplicates-not-computed">
            <p className="text-gray-600 dark:text-gray-300 text-sm">
                {checkState === 'PARTIAL'
                    ? `Проверена только часть гардероба: охват индексации ${coverage}%.`
                    : 'Дубли не проверяли: индекс гардероба не построен.'}
                {unindexed > 0 && ` Без индекса: ${pluralItems(unindexed)}.`}
            </p>
            {coverageLine}
            {indexLink}
        </div>
    );

    if (groups.length === 0) return (
        <div className="space-y-2">
            <p className="text-gray-400 text-sm" data-testid="duplicates-empty">
                Проверено — совпадений нет: взаимозаменяемых вещей в гардеробе не нашлось. 👍
            </p>
            {coverageLine}
        </div>
    );

    return (
        <div className="space-y-4" data-testid="duplicates-list">
            {coverageLine}
            {unindexedHint}
            {groups.map((g, i) => (
                <div key={i} className="rounded-xl border border-gray-200 dark:border-ink-700 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex flex-wrap items-center gap-2">
                        <span>{[g.categoryName, g.brandName].filter(Boolean).join(' · ') || 'Группа дублей'}</span>
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            {g.count} шт.
                        </span>
                        {g.duplicateType && (
                            <span data-testid="duplicate-type"
                                className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                {DUPLICATE_TYPE_LABELS[g.duplicateType] ?? 'Похожие вещи'}
                            </span>
                        )}
                        {g.similarity != null && g.similarity > 0 && (
                            <span data-testid="duplicate-similarity"
                                className="text-[11px] font-normal text-gray-500 dark:text-gray-400">
                                {Math.round(g.similarity * 100)}% сходства
                            </span>
                        )}
                    </h3>
                    <ul className="space-y-2">
                        {g.items.map(item => (
                            <ItemRow key={item.id} item={{
                                id: item.id,
                                name: item.name,
                                brandName: item.brandName,
                                categoryName: item.categoryName,
                                primaryPhotoUrl: item.primaryPhotoUrl,
                                totalWears: item.totalWears,
                                daysSinceWorn: null,
                                reason: null,
                                costPerWear: null,
                            }}
                            extra={item.totalWears != null ? pluralWears(item.totalWears) : undefined}
                            actionRoute={`/clothing/${item.id}/ai`}
                            actionLabel="Найти дубли"/>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};

// ── Recommendations Tab ───────────────────────────────────────────────────────

const RecommendationsTab: React.FC<{rec: Recommendations}> = ({rec}) => (
    <div className="space-y-5">
        {rec.dispose.length > 0 && (
            <div className="rounded-xl border border-rose-200 dark:border-rose-800/50 p-4 bg-rose-50 dark:bg-rose-900/10">
                <h3 className="text-sm font-semibold text-rose-700 dark:text-rose-400 mb-3">
                    🗑 Избавиться ({rec.dispose.length})
                </h3>
                <ul className="space-y-2">
                    {rec.dispose.map(item => (
                        <ItemRow key={item.id} item={item} extra={item.reason ?? undefined}/>
                    ))}
                </ul>
            </div>
        )}

        {rec.buy.length > 0 && (
            <div className="rounded-xl border border-sky-200 dark:border-sky-800/50 p-4 bg-sky-50 dark:bg-sky-900/10">
                <h3 className="text-sm font-semibold text-sky-700 dark:text-sky-400 mb-3">
                    🛒 Купить ({rec.buy.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                    {rec.buy.map(cat => (
                        <span key={cat}
                            className="px-3 py-1.5 rounded-full bg-sky-100 dark:bg-sky-900/30 text-sm text-sky-700 dark:text-sky-300">
                            {cat}
                        </span>
                    ))}
                </div>
            </div>
        )}

        {rec.wearMore.length > 0 && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 p-4 bg-amber-50 dark:bg-amber-900/10">
                <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-3">
                    ✨ Использовать чаще ({rec.wearMore.length})
                </h3>
                <ul className="space-y-2">
                    {rec.wearMore.map(item => (
                        <ItemRow key={item.id} item={item} extra={item.reason ?? undefined}/>
                    ))}
                </ul>
            </div>
        )}

        {rec.dispose.length === 0 && rec.buy.length === 0 && rec.wearMore.length === 0 && (
            <p className="text-gray-400 text-sm">
                Гардероб в хорошей форме — рекомендаций нет. Добавьте больше вещей с данными о носке, чтобы появились персональные советы.
            </p>
        )}
    </div>
);

export default Insights;
